import { ChangeDetectorRef, OnDestroy, Input, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoYTService } from '../../../../../../../fikoyt.service';
import { FikoSecililer } from '../../FikoSecililer';


@Component({
  selector: 'fikoyt-draft-angle',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './draft-angle.component.html',
  styleUrls: ['./draft-angle.component.scss']
})
export class DraftAngleComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: any = null;

  subsList: Subscription[] = [];
  haritaOlaylar: Map<string, any> = new Map();

  // Draft angle analizi özellikleri
  shaderMaterial: THREE.ShaderMaterial | null = null;
  aktifMeshler: THREE.Mesh[] = [];
  orijinalMaterials: Map<THREE.Mesh, THREE.Material> = new Map();
  moldDirection: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
  analizAktif: boolean = false;

  // Yüzey seçimi
  yonSecimAktif: boolean = false;
  fikoSecimYuzey: any = null;
  secilenFaceNormal: THREE.Vector3 | null = null;

  // Açı aralıkları listesi
  aciAraliklarListesi: {
    min: number;
    max: number;
    renk: string;
    isim: string;
  }[] = [
      { min: -180, max: -.3, renk: '#ff0000', isim: 'Ters Açı (Negatif)' },
      { min: 0, max: 180, renk: '#00ff00', isim: 'İyi Açı (Pozitif)' }
    ];

  yeniAralik = { min: 0, max: 10, renk: '#ffffff', isim: 'Yeni Aralık' };

  constructor(public readonly FikoYT: FikoYTService, public readonly CD: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.shaderOlustur();
  }

  public baslat(anaGosterim?: any) {
    this.anaGosterim = anaGosterim;

    if (!this.shaderMaterial) {
      this.shaderOlustur();
    }

    // FikoOlcumler mesh'lerini raycast'ten kapat
    if (this.anaGosterim?.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child instanceof THREE.Mesh &&
          (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
          child.userData.raycastable = false;
        }
      });
    }

    this.CD.detectChanges();
  }

  public bitir() {
    this.analiziDurdur();
    this.yuzeySecimiDurdur();

    // FikoOlcumler'leri geri raycast'e aç
    if (this.anaGosterim?.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child instanceof THREE.Mesh &&
          (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
          child.userData.raycastable = true;
        }
      });
    }
  }

  private shaderOlustur() {
    const sortedRanges = [...this.aciAraliklarListesi].sort((a, b) => a.min - b.min);
    const maxRanges = 10;
    const ranges = new Float32Array(maxRanges * 2);
    const colors = new Float32Array(maxRanges * 3);

    for (let i = 0; i < maxRanges; i++) {
      if (i < sortedRanges.length) {
        const range = sortedRanges[i];
        ranges[i * 2] = range.min * Math.PI / 180;
        ranges[i * 2 + 1] = range.max * Math.PI / 180;

        const color = new THREE.Color(range.renk);
        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
      } else {
        ranges[i * 2] = 999.0;
        ranges[i * 2 + 1] = 999.0;
        colors[i * 3] = 0.0;
        colors[i * 3 + 1] = 0.0;
        colors[i * 3 + 2] = 0.0;
      }
    }

    this.shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        moldDirection: { value: this.moldDirection.clone() },
        aciAraliklari: { value: ranges },
        renkler: { value: colors },
        aralikSayisi: { value: Math.min(sortedRanges.length, maxRanges) }
      },
      side: THREE.DoubleSide,
      transparent: true,
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        varying vec3 vWorldNormal;
        varying vec3 vWorldPosition;
        varying vec3 vViewPosition;

        void main(){
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;

          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldNormal = normalize(mat3(modelMatrix) * normal);
          vWorldPosition = worldPosition.xyz;

          vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
          vViewPosition = viewPosition.xyz;

          gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 moldDirection;
        uniform float aciAraliklari[20];
        uniform float renkler[30];
        uniform int aralikSayisi;

        varying vec3 vNormal;
        varying vec3 vPosition;
        varying vec3 vWorldNormal;
        varying vec3 vWorldPosition;
        varying vec3 vViewPosition;

        void main(){
          vec3 worldNormal = normalize(vWorldNormal);
          vec3 moldDir = normalize(moldDirection);

          float dotProduct = dot(worldNormal, moldDir);
          float aciRad = acos(clamp(dotProduct, -1.0, 1.0));

          float signedAci = aciRad;
          if(dotProduct < 0.0) {
            signedAci = -aciRad;
          }

          vec3 color = vec3(0.5, 0.5, 0.5);
          float alpha = 1.0;
          bool found = false;

          for(int i = 0; i < 10; i++) {
            if(i >= aralikSayisi) break;

            float minAci = aciAraliklari[i * 2];
            float maxAci = aciAraliklari[i * 2 + 1];

            if(signedAci >= minAci && signedAci <= maxAci) {
              color = vec3(
                renkler[i * 3],
                renkler[i * 3 + 1],
                renkler[i * 3 + 2]
              );
              found = true;
              break;
            }
          }

          if(!found && aralikSayisi > 1) {
            for(int i = 0; i < 9; i++) {
              if(i >= aralikSayisi - 1) break;

              float max1 = aciAraliklari[i * 2 + 1];
              float min2 = aciAraliklari[(i + 1) * 2];

              if(signedAci > max1 && signedAci < min2) {
                float t = (signedAci - max1) / (min2 - max1);
                vec3 color1 = vec3(renkler[i * 3], renkler[i * 3 + 1], renkler[i * 3 + 2]);
                vec3 color2 = vec3(renkler[(i + 1) * 3], renkler[(i + 1) * 3 + 1], renkler[(i + 1) * 3 + 2]);
                color = mix(color1, color2, t);
                break;
              }
            }
          }

          // Lighting - 3 nokta ışık sistemi
          vec3 viewDir = normalize(-vViewPosition);
          vec3 viewNormal = normalize(vNormal);

          // Key light (ana ışık - üst sol)
          vec3 keyLight = normalize(vec3(0.7, 1.0, 0.5));
          float keyDiffuse = max(dot(viewNormal, keyLight), 0.0);

          // Fill light (dolgu ışık - sağ)
          vec3 fillLight = normalize(vec3(-0.5, 0.3, 0.5));
          float fillDiffuse = max(dot(viewNormal, fillLight), 0.0) * 0.4;

          // Rim light (kenar ışık - arka)
          vec3 rimLight = normalize(vec3(0.0, 0.0, -1.0));
          float rimDiffuse = max(dot(viewNormal, rimLight), 0.0) * 0.3;

          // Ambient occlusion (basit curvature-based)
          vec3 dx = dFdx(vNormal);
          vec3 dy = dFdy(vNormal);
          float curvature = length(dx) + length(dy);
          float ao = 1.0 - clamp(curvature * 3.0, 0.0, 0.5);

          // Toplam ışık
          float totalLight = 0.3 + (keyDiffuse * 0.6) + fillDiffuse + rimDiffuse;
          totalLight *= ao;
          totalLight = clamp(totalLight, 0.3, 1.2);

          // Edge detection
          float edge = clamp(curvature * 2.0, 0.0, 1.0);

          // Final color with lighting
          color = color * totalLight;
          color = mix(color, color * 0.6, edge * 0.3);

          gl_FragColor = vec4(color, alpha);
        }
      `
    });
  }

  public analiziBaslat() {
    const seciliMeshler = this.seciliMeshleriAl();
    if (!this.shaderMaterial || seciliMeshler.length === 0) {
      return;
    }

    this.analiziDurdur();

    seciliMeshler.forEach((mesh: THREE.Mesh) => {
      if (mesh instanceof THREE.Mesh && mesh.material) {
        this.orijinalMaterials.set(mesh, mesh.material as THREE.Material);

        const geometry = mesh.geometry as THREE.BufferGeometry;
        if (!geometry.hasAttribute('color')) {
          const positionAttr = geometry.getAttribute('position');
          if (positionAttr) {
            const vertexCount = positionAttr.count;
            const colors = new Float32Array(vertexCount * 3);
            geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          }
        }

        mesh.material = this.shaderMaterial;

        if (mesh.parent) {
          mesh.parent.userData.analizMeshParent = true;
        }

        this.aktifMeshler.push(mesh);
      }
    });

    this.analizAktif = true;
    this.CD.detectChanges();
  }

  // B-Rep yüz-bazlı sayısal draft raporu (analitik normal — CATIA feasibility/undercut)
  brepRapor: { toplam: number; gruplar: { isim: string; renk: string; sayi: number; ters: boolean }[]; minDraftDeg: number } | null = null;

  /** Her B-Rep yüzünün analitik normalini çekme yönüyle karşılaştırıp yüz sayılarını + en küçük draft açısını raporlar (shader eşikleriyle tutarlı). */
  public brepDraftRaporuOlustur(): void {
    const meshler = this.aktifMeshler.length ? this.aktifMeshler : this.seciliMeshleriAl();
    const moldDir = this.moldDirection.clone().normalize();
    const ranges = [...this.aciAraliklarListesi].sort((a, b) => a.min - b.min);
    const sayim = new Map<string, number>();
    let toplam = 0, minDraftDeg = 90;
    const nm = new THREE.Matrix3(), v = new THREE.Vector3();
    for (const mesh of meshler) {
      const fg: any[] = (mesh as any).occt_faceGroups || [];
      if (!fg.length) continue;
      mesh.updateMatrixWorld?.(true);
      nm.getNormalMatrix(mesh.matrixWorld);
      for (const g of fg) {
        const gn = g?.normal; if (!gn) continue;
        const dot = THREE.MathUtils.clamp(v.set(gn.x, gn.y, gn.z).applyMatrix3(nm).normalize().dot(moldDir), -1, 1);
        const signedDeg = (dot < 0 ? -1 : 1) * Math.acos(dot) * 180 / Math.PI;   // shader signedAci ile aynı
        const r = ranges.find(rr => signedDeg >= rr.min && signedDeg <= rr.max);
        const isim = r?.isim || 'Aralık dışı';
        sayim.set(isim, (sayim.get(isim) || 0) + 1);
        toplam++;
        const draftDeg = 90 - Math.acos(Math.abs(dot)) * 180 / Math.PI;   // yüzün çekme düzlemine eğimi (0=dik duvar, kritik)
        if (draftDeg < minDraftDeg) minDraftDeg = draftDeg;
      }
    }
    if (!toplam) { this.brepRapor = null; this.CD.detectChanges(); return; }
    const gruplar = ranges.filter(r => sayim.has(r.isim)).map(r => ({ isim: r.isim, renk: r.renk, sayi: sayim.get(r.isim) || 0, ters: r.min < 0 }));
    if (sayim.has('Aralık dışı')) gruplar.push({ isim: 'Aralık dışı', renk: '#888888', sayi: sayim.get('Aralık dışı')!, ters: false });
    this.brepRapor = { toplam, gruplar, minDraftDeg: +minDraftDeg.toFixed(2) };
    this.CD.detectChanges();
  }

  public analiziDurdur() {
    this.aktifMeshler.forEach(mesh => {
      const orijinalMaterial = this.orijinalMaterials.get(mesh);
      if (orijinalMaterial) {
        mesh.material = orijinalMaterial;
      }

      if (mesh.parent && mesh.parent.userData?.analizMeshParent) {
        delete mesh.parent.userData.analizMeshParent;
      }
    });

    this.aktifMeshler = [];
    this.orijinalMaterials.clear();
    this.analizAktif = false;

    this.CD.detectChanges();
  }

  private seciliMeshleriAl(): THREE.Mesh[] {
    const meshler: THREE.Mesh[] = [];

    if (!this.anaGosterim) {
      return meshler;
    }

    const seciliGruplar = [];

    if (this.anaGosterim.secililer?.grupSecimlerUzerinde) {
      seciliGruplar.push(this.anaGosterim.secililer.grupSecimlerUzerinde);
    }
    if (this.anaGosterim.secililer?.grupSecimler2Uzerinde) {
      seciliGruplar.push(this.anaGosterim.secililer.grupSecimler2Uzerinde);
    }

    seciliGruplar.forEach((grup: any) => {
      if (grup && grup.children) {
        grup.traverse((child: any) => {
          if (child instanceof THREE.Mesh && child.material) {
            meshler.push(child);
          }
        });
      }
    });

    if (meshler.length === 0 && this.anaGosterim.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((child: any) => {
        if (child instanceof THREE.Mesh &&
          child.visible &&
          child.name !== 'Ölçümler' &&
          child.userData.type !== 'FikoOlcumler') {
          meshler.push(child);
        }
      });
    }

    return meshler;
  }

  public moldYonunuGuncelle(eksen: 'x' | 'y' | 'z') {
    this.secilenFaceNormal = null;

    switch (eksen) {
      case 'x':
        this.moldDirection = new THREE.Vector3(1, 0, 0);
        break;
      case 'y':
        this.moldDirection = new THREE.Vector3(0, 1, 0);
        break;
      case 'z':
        this.moldDirection = new THREE.Vector3(0, 0, 1);
        break;
    }

    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.moldDirection.value = this.moldDirection.clone();
    }

    this.CD.detectChanges();
  }

  public yonSec() {
    this.yuzeySecimiBaslat();
  }

  public resetYuzeySecimi() {
    this.secilenFaceNormal = null;
    this.moldDirection = new THREE.Vector3(0, 0, 1);

    if (this.shaderMaterial) {
      this.shaderMaterial.uniforms.moldDirection.value = this.moldDirection.clone();
    }

    this.yuzeySecimiBaslat();
    this.CD.detectChanges();
  }

  public aralikEkle() {
    this.aciAraliklarListesi.push({
      min: this.yeniAralik.min,
      max: this.yeniAralik.max,
      renk: this.yeniAralik.renk,
      isim: this.yeniAralik.isim
    });

    this.yeniAralik = { min: 0, max: 10, renk: '#ffffff', isim: 'Yeni Aralık' };
    this.shaderiGuncelle();
  }

  public aralikSil(index: number) {
    this.aciAraliklarListesi.splice(index, 1);
    this.shaderiGuncelle();
  }

  public aralikDegisti() {
    this.shaderiGuncelle();
  }

  private shaderiGuncelle() {
    if (!this.shaderMaterial) return;

    const sortedRanges = [...this.aciAraliklarListesi].sort((a, b) => a.min - b.min);
    const maxRanges = 10;
    const ranges = new Float32Array(maxRanges * 2);
    const colors = new Float32Array(maxRanges * 3);

    for (let i = 0; i < maxRanges; i++) {
      if (i < sortedRanges.length) {
        const range = sortedRanges[i];
        ranges[i * 2] = range.min * Math.PI / 180;
        ranges[i * 2 + 1] = range.max * Math.PI / 180;

        const color = new THREE.Color(range.renk);
        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
      } else {
        ranges[i * 2] = 999.0;
        ranges[i * 2 + 1] = 999.0;
        colors[i * 3] = 0.0;
        colors[i * 3 + 1] = 0.0;
        colors[i * 3 + 2] = 0.0;
      }
    }

    this.shaderMaterial.uniforms.aciAraliklari.value = ranges;
    this.shaderMaterial.uniforms.renkler.value = colors;
    this.shaderMaterial.uniforms.aralikSayisi.value = Math.min(sortedRanges.length, maxRanges);

    this.CD.detectChanges();
  }

  private yuzeySecimiBaslat() {
    if (!this.anaGosterim || this.fikoSecimYuzey?.aktif) {
      return;
    }

    const seciliMeshler = this.seciliMeshleriAl();
    if (seciliMeshler.length === 0) {
      return;
    }

    if (this.fikoSecimYuzey) {
      this.fikoSecimYuzey.bitir();
      this.fikoSecimYuzey = null;
    }

    this.fikoSecimYuzey = new FikoSecililer(this.anaGosterim);
    this.fikoSecimYuzey.rayObje = seciliMeshler;
    this.fikoSecimYuzey.filitreler = ["Mesh"];
    this.fikoSecimYuzey.raycaster.params.Mesh = { threshold: 0 };

    const sub = this.fikoSecimYuzey.tiklandi.subscribe((kesisim: any) => {
      let faceNormal: THREE.Vector3 | null = null;
      const actualMesh = kesisim?.altObje || kesisim?.object;
      const mesh = actualMesh as THREE.Mesh;

      if (kesisim?.face?.normal) {
        const localNormal = kesisim.face.normal.clone();
        faceNormal = localNormal.applyMatrix3(
          new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
        ).normalize();
      } else if (mesh.geometry && kesisim.faceIndex !== undefined) {
        const geometry = mesh.geometry as THREE.BufferGeometry;
        const normalAttr = geometry.getAttribute('normal') as THREE.BufferAttribute;
        if (normalAttr) {
          const faceIndex = kesisim.faceIndex;
          const normalArray = new Float32Array(3);
          normalArray[0] = normalAttr.getX(faceIndex);
          normalArray[1] = normalAttr.getY(faceIndex);
          normalArray[2] = normalAttr.getZ(faceIndex);
          const localNormal = new THREE.Vector3(normalArray[0], normalArray[1], normalArray[2]);
          faceNormal = localNormal.applyMatrix3(
            new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
          ).normalize();
        }
      }

      if (faceNormal) {
        this.secilenFaceNormal = faceNormal.clone();
        this.moldDirection = faceNormal.clone();

        if (this.shaderMaterial) {
          this.shaderMaterial.uniforms.moldDirection.value = this.moldDirection.clone();
        }

        this.yuzeySecimiDurdur();
        this.CD.detectChanges();
      }
    });

    this.subsList.push(sub);

    this.fikoSecimYuzey.baslat();
    this.fikoSecimYuzey.aktif = true;

    this.yonSecimAktif = true;
    this.CD.detectChanges();
  }

  private yuzeySecimiDurdur() {
    if (this.fikoSecimYuzey) {
      this.fikoSecimYuzey.aktif = false;
      this.fikoSecimYuzey.bitir();
      this.fikoSecimYuzey = null;
    }
    this.yonSecimAktif = false;
    this.CD.detectChanges();
  }

  ngOnDestroy(): void {
    this.analiziDurdur();

    if (this.shaderMaterial) {
      this.shaderMaterial.dispose();
      this.shaderMaterial = null;
    }

    this.orijinalMaterials.clear();
    this.aktifMeshler = [];

    this.subsList.forEach(element => {
      if (element && element.unsubscribe) {
        element.unsubscribe();
      }
    });

    this.haritaOlaylar.forEach((value: any, key: string) => {
      if (value && typeof value === 'function') {
        value();
      }
    });
    this.haritaOlaylar.clear();

    this.anaGosterim = null;
    this.secilenFaceNormal = null;
  }
}
